<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  campaignId: {
    type: Number,
    default: 0,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  conversationsCount: {
    type: Number,
    default: 0,
  },
  totalContacts: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => {
  if (props.status === 'paused') return 'text-n-amber-11';
  return {
    'text-n-teal-11': isActive.value,
    'text-n-slate-12': !isActive.value,
  };
});

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === 'paused') {
    return t('CAMPAIGN.STATUS.PAUSED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  const isScheduledInFuture = props.scheduledAt * 1000 > Date.now();
  return isScheduledInFuture
    ? t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED')
    : t('CAMPAIGN.SMS.CARD.STATUS.IN_PROGRESS');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
    provider,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled, provider);
});

const handleResume = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    await store.dispatch('campaigns/update', {
      id: props.campaignId,
      campaign_status: 'active',
    });
    useAlert(t('CAMPAIGN.STATUS.RESUMED'));
  } catch {
    useAlert(t('CAMPAIGN.STATUS.RESUME_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handleStop = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    await store.dispatch('campaigns/update', {
      id: props.campaignId,
      campaign_status: 'completed',
    });
    useAlert(t('CAMPAIGN.STATUS.STOPPED'));
  } catch {
    useAlert(t('CAMPAIGN.STATUS.STOP_ERROR'));
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
          :conversations-count="conversationsCount"
          :total-contacts="totalContacts"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        v-if="status === 'paused'"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-play"
        :disabled="isLoading"
        :is-loading="isLoading"
        @click="handleResume"
      />
      <Button
        v-if="status === 'paused'"
        variant="faded"
        size="sm"
        color="ruby"
        icon="i-lucide-square"
        :disabled="isLoading"
        :is-loading="isLoading"
        @click="handleStop"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
